import { stateful } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';

export type MicrosoftSyncStatus = 'loading' | 'synced' | 'drift';

/**
 * Layout of a Microsoft tab: the two cards side by side from the md breakpoint on, stretched to the
 * same height and filling the screen.
 *
 * `items-stretch` is what makes the two cards equal height - without it each one ends at its own
 * content and the pair looks ragged, with the connector between them dangling in mid air.
 *
 * ponytail: the `13rem` is the app bar plus the tab strip plus the container margin, hard coded because
 * nothing in the page shell establishes a height chain the cards could inherit. If the header ever
 * grows, this makes the cards a little too tall rather than breaking - `min-h` only ever adds. The
 * upgrade is a flex column on the page shell, at which point this becomes `grow`.
 *
 * `pb-28` keeps the last rows clear of the fixed save bar at the bottom of the viewport.
 *
 * `shrink-0` because the container is a flex item of the page's scrolling column: an explicit `min-h`
 * replaces the content-based minimum, so taller content squeezed the container down to that height and
 * the rows past it - `pb-28` included - ended up under the save bar.
 */
export const MICROSOFT_TAB_LAYOUT = 'md:flex-row items-stretch md:min-h-[calc(100vh-13rem)] pb-28 shrink-0';

/**
 * The visual link between the two cards: our database on one side, Entra on the other.
 *
 * It carries the same answer the badges carry, in a form you read without reading - dots flowing
 * between the cards when the two sides agree, a severed line when they do not. That is the whole point
 * of it sitting *between* them rather than inside one: the tab is about the relationship of two
 * systems, and the gap between the cards is where that relationship lives.
 *
 * The styling is a local `<style>` block rather than utility classes because the flow needs its own
 * keyframes, and a one-off animation is not worth a token in the app-wide theme. Colours come from the
 * daisyUI theme variables, so the indicator follows the theme like everything else.
 */
export const MicrosoftSyncIndicator = stateful((props: { status: MicrosoftSyncStatus; differences: number }) => {
  const label =
    props.status === 'loading'
      ? 'Abgleich mit Entra läuft'
      : props.status === 'synced'
        ? 'Portal und Entra sind synchron'
        : `${props.differences} ${props.differences === 1 ? 'Wert weicht' : 'Werte weichen'} von Entra ab`;

  return html`
    <style>
      .ms-sync {
        /* how far a dot travels before it fades - half the rail, so dots vanish under the medallion */
        --ms-sync-travel: 2.25rem;
        --ms-sync-tone: var(--color-base-content);

        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        /* stacked cards: a short, wide channel. side by side cards: a narrow, tall one. */
        width: 100%;
        height: 4rem;
      }

      .ms-sync[data-state='synced'] {
        --ms-sync-tone: var(--color-success);
      }

      .ms-sync[data-state='drift'] {
        --ms-sync-tone: var(--color-error);
      }

      /* the cable between the two cards */
      .ms-sync-rail {
        position: absolute;
        inset-inline: 0;
        top: 50%;
        height: 2px;
        transform: translateY(-50%);
        background: color-mix(in oklab, var(--ms-sync-tone) 35%, transparent);
        border-radius: 999px;
      }

      /* out of sync: the cable is cut. Dashes read as "interrupted" even without the colour. */
      .ms-sync[data-state='drift'] .ms-sync-rail {
        background: repeating-linear-gradient(
          to right,
          color-mix(in oklab, var(--ms-sync-tone) 55%, transparent) 0 6px,
          transparent 6px 12px
        );
      }

      .ms-sync-dots {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .ms-sync-dot {
        position: absolute;
        width: 0.4rem;
        height: 0.4rem;
        border-radius: 999px;
        background: var(--ms-sync-tone);
        opacity: 0;
        animation: ms-sync-flow-x 2.4s linear infinite;
      }

      .ms-sync-dot:nth-child(2) {
        animation-delay: 0.8s;
      }

      .ms-sync-dot:nth-child(3) {
        animation-delay: 1.6s;
      }

      /* nothing flows while the two sides disagree - the stillness is the message */
      .ms-sync[data-state='drift'] .ms-sync-dot {
        animation: none;
        opacity: 0;
      }

      .ms-sync[data-state='loading'] .ms-sync-dot {
        animation-duration: 3.6s;
      }

      @keyframes ms-sync-flow-x {
        0% {
          transform: translateX(calc(var(--ms-sync-travel) * -1));
          opacity: 0;
        }
        25%,
        75% {
          opacity: 1;
        }
        100% {
          transform: translateX(var(--ms-sync-travel));
          opacity: 0;
        }
      }

      @keyframes ms-sync-flow-y {
        0% {
          transform: translateY(calc(var(--ms-sync-travel) * -1));
          opacity: 0;
        }
        25%,
        75% {
          opacity: 1;
        }
        100% {
          transform: translateY(var(--ms-sync-travel));
          opacity: 0;
        }
      }

      /* the hub. Sits on top of the rail with an opaque background, so dots disappear behind it. */
      .ms-sync-hub {
        position: relative;
        display: grid;
        place-items: center;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 999px;
        background: var(--color-base-100);
        color: var(--ms-sync-tone);
        border: 2px solid color-mix(in oklab, var(--ms-sync-tone) 45%, transparent);
        box-shadow: 0 0 0 6px color-mix(in oklab, var(--ms-sync-tone) 8%, transparent);
      }

      /* a slow breath while the two sides disagree, so the eye is drawn to the repair button */
      .ms-sync[data-state='drift'] .ms-sync-hub {
        animation: ms-sync-breathe 2s ease-in-out infinite;
      }

      @keyframes ms-sync-breathe {
        0%,
        100% {
          box-shadow: 0 0 0 6px color-mix(in oklab, var(--ms-sync-tone) 8%, transparent);
        }
        50% {
          box-shadow: 0 0 0 12px color-mix(in oklab, var(--ms-sync-tone) 2%, transparent);
        }
      }

      /* side by side cards from the md breakpoint on: the channel turns vertical */
      @media (min-width: 48rem) {
        .ms-sync {
          width: 4rem;
          height: auto;
          align-self: stretch;
        }

        .ms-sync-rail {
          inset-inline: auto;
          inset-block: 0;
          top: auto;
          left: 50%;
          width: 2px;
          height: auto;
          transform: translateX(-50%);
        }

        .ms-sync[data-state='drift'] .ms-sync-rail {
          background: repeating-linear-gradient(
            to bottom,
            color-mix(in oklab, var(--ms-sync-tone) 55%, transparent) 0 6px,
            transparent 6px 12px
          );
        }

        .ms-sync-dot {
          animation-name: ms-sync-flow-y;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .ms-sync-dot {
          animation: none;
          opacity: 0.6;
        }

        .ms-sync[data-state='drift'] .ms-sync-hub {
          animation: none;
        }
      }
    </style>

    <div class="ms-sync" data-state=${props.status} role="status" aria-label=${label} title=${label}>
      <div class="ms-sync-rail"></div>
      <div class="ms-sync-dots">
        <span class="ms-sync-dot"></span>
        <span class="ms-sync-dot"></span>
        <span class="ms-sync-dot"></span>
      </div>
      <div class="ms-sync-hub">
        ${
          props.status === 'loading'
            ? html`<span class="loading loading-spinner loading-sm"></span>`
            : props.status === 'synced'
              ? html`<span class="icon-[material-symbols--check-circle-rounded] text-xl"></span>`
              : html`<span class="icon-[material-symbols--link-off] text-xl"></span>`
        }
      </div>
    </div>
  `;
});
